import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  SignalWifi4Bar,
  SignalWifi3Bar,
  SignalWifi2Bar,
  SignalWifi1Bar
} from '@mui/icons-material';
import { useSession } from '../context/SessionContext';

const ConnectionStatus = () => {
  const { socket, isConnected } = useSession();
  const [connectionQuality, setConnectionQuality] = useState('good');
  const [latency, setLatency] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Ping test for connection quality
  useEffect(() => {
    if (!socket || !isConnected) return;

    const pingInterval = setInterval(() => {
      const startTime = Date.now();
      socket.emit('ping', startTime, () => {
        const roundTripTime = Date.now() - startTime;
        setLatency(roundTripTime);

        // Determine connection quality
        if (roundTripTime < 100) {
          setConnectionQuality('excellent');
        } else if (roundTripTime < 300) {
          setConnectionQuality('good');
        } else if (roundTripTime < 1000) {
          setConnectionQuality('poor');
        } else {
          setConnectionQuality('bad');
        }
      });
    }, 10000); // Ping every 10 seconds

    return () => clearInterval(pingInterval);
  }, [socket, isConnected]);

  // Track reconnecting state
  useEffect(() => {
    if (!isConnected && socket) {
      setReconnecting(true);
    } else if (isConnected) {
      setReconnecting(false);
    }
  }, [isConnected, socket]);

  const getStatusColor = () => {
    switch (connectionQuality) {
      case 'excellent': return 'success';
      case 'good': return 'success';
      case 'poor': return 'warning';
      case 'bad': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (connectionQuality) {
      case 'excellent': return <SignalWifi4Bar />;
      case 'good': return <SignalWifi3Bar />;
      case 'poor': return <SignalWifi2Bar />;
      case 'bad': return <SignalWifi1Bar />;
      default: return <CheckCircle />;
    }
  };

  const getStatusText = () => {
    if (reconnecting) return 'Reconnecting...';
    return `Connected${latency ? ` (${latency}ms)` : ''}`;
  };

  // Hide the status indicator when disconnected - reconnection happens automatically
  if (!isConnected && !reconnecting) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        icon={reconnecting ? <CircularProgress size={16} /> : getStatusIcon()}
        label={getStatusText()}
        color={getStatusColor()}
        size="small"
        variant="filled"
      />
    </Box>
  );
};

export default ConnectionStatus; 